/**
 * The **unified action registry** — the one per-combatant enumeration seam the combat action bar
 * reads. It answers a single question: *what actions can THIS combatant take right now?* — and returns
 * them as a flat list of {@link ActionDescriptor}, each carrying a {@link MechActionRoute}.
 *
 * The list is the union of three sources, merged into one shape so the bar needs no second discovery
 * path for loadout-granted actions:
 *   - **core** — the combatant's frozen `base.coreActionIds`, each a provenance-namespaced composite
 *     (`"mech:skirmish"`, `"npc:overwatch"`) resolved against the global core-action catalog
 *     (`coreActionCatalog.ts`) and tagged with its `route` + optional `value` badge kind (e.g. Boost's
 *     `speed`). **The registry reads the combatant's own frozen set, never its `kind`**: the spawn
 *     adapter assembled the set, so a new acting-kind costs only its spawn adapter. The outward
 *     descriptor carries the **bare** id (`"skirmish"`) — composite-internal, bare-external.
 *   - **granted** — the mech's frozen `base.affordances` granted records (composite `<itemId>:<actionIndex>` keys), unioned with the enumerate-time overlay mint by `carriedAffordances`: each rendered
 *     by looking the id up in the **compendium** (name / activation / detail — ids-only, never frozen
 *     copy), its `route` derived from the action's activation, and its `resolvable` flag **derived**
 *     (does `SEED_OVERLAY[id]` carry an authored `action` effect?). An unauthored granted action
 *     surfaces as a non-`resolvable` advisory "GM adjudicates" row — the bar must NOT send a
 *     `request_activate` for it (the resolver throws on a missing overlay entry).
 *   - **pilot effects** — the pilot's frozen `base.pilotEffectIds` (`spawn.ts`'s `freezePilotEffectIds`:
 *     talent-rank + core-bonus ids). Unlike the granted records' keys, these are **bare** overlay keys
 *     (`"t_crack_shot:1"`, talent id + rank — NOT an `<itemId>:<actionIndex>` composite), so they
 *     bypass `parseGrantedId`/`lookupGranted`/the destroyed-system filter entirely and are looked up
 *     directly against the `talent`/`core_bonus` compendium categories for display. Only ids that
 *     actually gather an `action`-kind overlay effect enumerate — a passive-only talent rank
 *     contributes nothing here (no advisory row; most talent ranks are passives, not actions).
 *
 * **No per-combatant overlay merge**: frozen ids look up against the single global `SEED_OVERLAY`,
 * exactly like `pilotEffectIds`. **Granted reactions are Category B**: a `route: "reaction"` granted
 * action is enumerated (so the bar can show it informationally) but a caller building the activate
 * list filters it out — it never becomes an activate affordance.
 *
 * **Pure** — no I/O, no React: the compendium + overlay are passed in. The client holds both
 * (`CompendiumProvider` + the shared `SEED_OVERLAY`); the server reads the overlay only.
 */

import type { Affordance, Combatant, EncounterState, TurnBudget, WeaponRange } from "@lancer-vtt/domain";
import { canUseAction, canUseProtocolAction, canUseRoundAction, canUseSceneAction, findAffordance, isEarmarkVerb, freeActionUses, grantedActionAllowanceBonus } from "@lancer-vtt/domain";
import type { DeployPlacement } from "./deploy";
import { evaluatePredicate, objectGroupOut, predicateActorRefFields, type Predicate } from "./effects/predicate";
import { parseRange, type RawWeapon } from "./weaponProfile";
import type { Compendium } from "@lancer-vtt/compendium";
import { type MechActionRoute, type MechActionValueKind } from "./mechActions";
import { type NpcActionValueKind } from "./npcActions";
import { type CoreAction } from "./coreActionCatalog";
import { gatherActions, type LoadedOverlay, type GatheredAction } from "./effects/loader";
import { SEED_OVERLAY, cannotMakeAttacks } from "./effects/seed";
import { frameEffectEntries } from "./spawn";
import { weaponActionBearer, type RawActionBearer } from "./spawn/actionFreeze";
import { carriedAffordances, isProtocolAction, parseGrantedId, resolvedCoreActions, carriedPilotFrameActionIds, carriedStatusActionIds, deployedActionSources } from "./economy";
import { isTechAttackAction } from "./techAttackAction";

/** Whether a descriptor came from the universal core table or a loadout-granted item. */
export type ActionSource = "core" | "granted";

/**
 * The UI targeting flow a (resolvable) action needs — the hint the bar reads to choose its affordance:
 * - `"self"` — no board target (Active Camouflage, Stabilize): a one-click `request_activate` with no target.
 * - `"single"` — one target token (Stasis Generator, an attack): arm → click a token → fire.
 * - `"cell"` — a board CELL, not a token (Smoke/HEX Charges thrown to a point — a `for_each origin:"site"`
 *   or `create_area origin:"site"` area action): arm → click a bare cell → fire with `RequestActivate.site`.
 * - `"sites"` — a directed multi-pick placement (`deploy_object`; ALSO `deploy_mine`'s fixed 1-pick
 *   degenerate case): arm → click `count` cells in turn (Jericho 2, the turret drone 1, a mine deploy
 *   ALWAYS 1) → fire with `RequestActivate.sites`. The accumulating board gesture is wired and
 *   browser-proven (`frontend/e2e/deployables.e2e.ts` drives the 1-pick mine case end to end). One wall
 *   the generic gesture does NOT derive for you: a new caller's extra placement constraint must be
 *   threaded into the client-side wash explicitly — see `deploy_mine`'s `mine` flag.
 * - `"advisory"` — a `deploy_object` atom whose `sectionCount` isn't (yet) a literal number at this
 *   UNBOUND overlay-read layer (a `{VAL}` template — see {@link directedDeployRequirement}). Never a real
 *   carrier today (Jericho and the turret both author a literal count), but the classification must not
 *   mint a live multi-pick affordance with no known end, so it degrades here instead of guessing. Distinct
 *   from {@link ActionDescriptor.resolvable}`=== false`'s unrelated "no authored overlay body at all"
 *   advisory row — this one DOES have an authored body, it just can't be armed safely.
 *
 * Derived for a *resolvable* granted action from its authored overlay atoms, most-specific first: a
 * `deploy_object`/`deploy_mine` atom ⇒ `"sites"`/`"advisory"` per {@link directedDeployRequirement}
 * (`deploy_mine` never returns `"advisory"` — its shape is structurally fixed); otherwise a
 * `for_each` selector or a `create_area` atom reading `origin: "site"` ⇒ `"cell"`; otherwise an atom
 * binding the `target` actor ref ⇒ `"single"`; else `"self"`. Cosmetic for a `resolvable: false` advisory
 * one (never fires an intent).
 */
export type ActionTargeting = "self" | "single" | "cell" | "sites" | "advisory";

/**
 * The directed-deploy **pick shape** for overlay action `id` — how many cells it requires and each
 * section's footprint size. The ONE place `targetingFromOverlay`'s browser row classification and the
 * server-side room gate both read, so the two can never independently disagree about the count/size —
 * the same discipline `checkDirectedDeployPlacement` applies atop `legalDirectedDeployCells` for the
 * placement RULE, one layer up, for the placement SHAPE.
 *
 * Reads the overlay **UNBOUND** (no `bindings` passed to {@link gatherActions}) — matching
 * `targetingFromOverlay`'s own read — so a `deploy_object` atom's `sectionCount`/`size` can still be an
 * unresolved `{VAL}` template HERE even though `DeployObjectAtom.sectionCount` is always a literal
 * `number` by the time `interpretDeployObject` reads it post-bind (`atoms.ts` — the loader binds any
 * authored template before the atom reaches the interpreter).
 *
 * Returns:
 * - `undefined` — `id` gathers no `deploy_object`/`deploy_mine` atom at all (not a directed-deploy
 *   action; every other `targetingFromOverlay` outcome stands).
 * - `"advisory"` — IS a `deploy_object` atom, but `sectionCount` (or an authored `size` override) isn't a
 *   literal number yet. Callers must never mint/enforce a multi-pick shape with no known end. (A
 *   `deploy_mine` atom never takes this arm — its pick shape is structurally fixed, see below.)
 * - `{ count, size }` — the literal pick count (Jericho 2, the turret drone 1, a mine deploy ALWAYS 1)
 *   and each section's footprint size (`atom.size ?? 1` for `deploy_object`; a mine is always Size 1,
 *   RAW's mine footprint — `deploy.ts`'s header comment). `mine: true` when this came from a
 *   `deploy_mine` atom — the discriminator the room gate and
 *   {@link import("./deploy").legalDirectedDeployCells}'s wash-enumeration key on to ALSO apply
 *   {@link import("./deploy").checkMineAdjacency}'s mine-only "not adjacent to any other mines" wall,
 *   which `deploy_object` carriers never run.
 */
export type DirectedDeployRequirement = { count: number; size: number; mine?: boolean; placement?: DeployPlacement };

/**
 * The live board a requirement read may consult: with it, an `if object_group_deployed` toggle
 * (Jericho's, a drone's deploy/recall row) follows only the branch the owner's group selects, so a
 * row whose group is already out asks for no placement. Without it, both branches are walked.
 */
export type DeployRequirementLiveness = { state: EncounterState; ownerId: string };

/** The structural fields {@link findDirectedDeployAtom} reads off a raw (untyped) atom node — the
 *  `deploy_object`/`deploy_mine` fields it's ultimately looking for, plus every nested-body field the
 *  DSL's control-flow atoms can carry one inside. */
type NestedAtomNode = {
  op?: unknown;
  sectionCount?: unknown;
  size?: unknown;
  within?: unknown;
  placement?: unknown;
  count?: unknown;
  origin?: unknown;
  pairWithin?: unknown;
  cond?: { op?: unknown; who?: unknown; groupId?: unknown; live?: unknown };
  then?: unknown[];
  else?: unknown[];
  effect?: unknown[];
  on_fail?: unknown[];
  on_pass?: unknown[];
  on_hit?: unknown[];
  on_miss?: unknown[];
  options?: { effect?: unknown[] }[];
};

/**
 * Depth-first search for the first `deploy_object` OR `deploy_mine` atom (or a two-space `create_area`
 * mark, `origin: "site_pair"`) anywhere in `atoms` —
 * recurses through every control-flow body the DSL can nest one inside: `if`'s `then`/`else`
 * (Jericho's REAL authored shape — the deploy atom sits inside the `else` of an
 * `if object_group_deployed … then recall_object else deploy_object`, toggling one "activate" row
 * between deploy and recall), `choose`'s `options[].effect`, `for_each`'s `effect`, `force_save`'s
 * `on_fail`/`on_pass`, `roll`'s `on_hit`/`on_miss`. Mirrors `lint.ts`'s `walkAtoms` recursion
 * shape, narrowed to two target ops with no predicate traversal (nothing here reads predicates).
 * **The recursion is load-bearing**: a flat top-level-only scan finds the turret's `deploy_object`
 * (authored top-level, no wrapper) but silently MISSES Jericho's nested one — a gap only a proof
 * against the real pack overlay catches, never a synthetic fixture that omits the wrapper.
 */
/** A `{ within: N }` placement, read off an untyped atom node. */
function isWithinPlacement(p: unknown): p is { within: number } {
  return typeof p === "object" && p !== null && typeof (p as { within?: unknown }).within === "number";
}

function findDirectedDeployAtom(atoms: readonly unknown[], live?: DeployRequirementLiveness): NestedAtomNode | undefined {
  for (const raw of atoms) {
    const atom = raw as NestedAtomNode;
    if (atom.op === "deploy_object" || atom.op === "deploy_mine") return atom;
    if (atom.op === "create_area" && atom.origin === "site_pair") return atom;
    const deployed = live !== undefined ? liveGroupToggle(atom, live) : undefined;
    const nestedBodies: (unknown[] | undefined)[] = [
      deployed === false ? undefined : atom.then,
      deployed === true ? undefined : atom.else,
      atom.effect,
      atom.on_fail,
      atom.on_pass,
      atom.on_hit,
      atom.on_miss,
      ...(atom.options ?? []).map((o) => o.effect),
    ];
    for (const body of nestedBodies) {
      if (!body) continue;
      const found = findDirectedDeployAtom(body, live);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * For an `if` gated on the owner's own `object_group_deployed`, whether that group is out right now;
 * `undefined` for any other node (both branches stay in play).
 */
function liveGroupToggle(atom: NestedAtomNode, live: DeployRequirementLiveness): boolean | undefined {
  const cond = atom.cond;
  if (atom.op !== "if" || cond?.op !== "object_group_deployed" || cond.who !== "self" || typeof cond.groupId !== "string") return undefined;
  return objectGroupOut(live.state, `${live.ownerId}:${cond.groupId}`, cond.live === true);
}

/**
 * The pick-shape derivation itself, over an ALREADY-gathered action list — split out of
 * {@link directedDeployRequirement} so a caller that already ran {@link gatherActions} for this id
 * (every enumeration site in this file does — see `enumerateActions`'s `gathered`) can reuse that
 * walk instead of re-gathering. `directedDeployRequirement` below is a thin wrapper over this for the
 * one external caller (`backend/src/encounterRoom.ts`'s room gate) that doesn't already have a
 * gathered list lying around. Both paths run the exact same derivation — this stays the ONE place the
 * pick shape is computed regardless of which entry point a caller uses.
 */
function directedDeployRequirementFromActions(
  actions: readonly GatheredAction[],
  live?: DeployRequirementLiveness,
): DirectedDeployRequirement | "advisory" | undefined {
  for (const a of actions) {
    const node = findDirectedDeployAtom(a.effect.effect, live);
    if (!node) continue;
    // A two-space mark (Accelerate): two Size 1 picks in line of sight, within `pairWithin` of each other.
    if (node.op === "create_area") {
      return typeof node.pairWithin === "number" ? { count: 2, size: 1, placement: { mutualWithin: node.pairWithin } } : "advisory";
    }
    // `deploy_mine`: Size 1 (a mine's footprint is RAW-fixed), one pick per mine (`count`, default
    // 1), adjacent unless the atom authors a ranged `placement`; never `"advisory"`.
    if (node.op === "deploy_mine") {
      const placement = node.placement === "sensors" ? ("sensors" as const) : isWithinPlacement(node.placement) ? { within: node.placement.within } : undefined;
      return { count: typeof node.count === "number" ? node.count : 1, size: 1, mine: true, ...(placement !== undefined ? { placement } : {}) };
    }
    // An area-scoped deploy (`within`) picks free cells inside a template, not cells adjacent to
    // the actor, so it is no directed deploy: the action targets as its other atoms say (Total
    // Strength Suite's target), and the interpreter's own wall and per-section `"cell"` prompts
    // place it.
    if (node.within !== undefined) return undefined;
    if (typeof node.sectionCount !== "number") return "advisory";
    if (node.size !== undefined && typeof node.size !== "number") return "advisory";
    return {
      count: node.sectionCount,
      size: typeof node.size === "number" ? node.size : 1,
      ...(node.placement === "sensors" ? { placement: "sensors" as const } : {}),
    };
  }
  return undefined;
}

/** Named arguments for {@link directedDeployRequirement} (`max-params`-3 discipline). */
export type DirectedDeployRequirementArgs = {
  ownerId: string;
  id: string;
  overlay: LoadedOverlay;
  /** The live board, for a deploy/recall toggle — see {@link DeployRequirementLiveness}. */
  state?: EncounterState;
};

export function directedDeployRequirement(args: DirectedDeployRequirementArgs): DirectedDeployRequirement | "advisory" | undefined {
  const { ownerId, id, overlay, state } = args;
  const actions = gatherActions({ ownerId, entityId: id, overlay });
  return directedDeployRequirementFromActions(actions, state ? { state, ownerId } : undefined);
}

/**
 * Can this predicate be answered from **the acting combatant and the board alone** — i.e. with
 * nothing bound but `self`?
 *
 * This exists because {@link enumerateActions} answers a clause's `condition` to decide whether its
 * row is available *before* the player has picked anything. At that moment there is no `target`, no
 * `attacker`, no recorded `spatial` answer and no resolution-scoped `choose` ledger — all four of
 * which {@link evaluatePredicate} reads as plain `false` rather than "unknown". So handing it an
 * undecidable predicate does not produce a wrong-ish answer, it produces a **confidently wrong**
 * one, and the row it grades goes dark forever. Two clauses shipped today would do exactly that:
 * `pg_patch` (`or(target_is self, spatial adjacent)`) and `pg_corrective` (`has_status … who:
 * "target"`) — both would read `false` and permanently grey out a piece of working pilot gear.
 *
 * So the filter **fails open**: an op this doesn't positively vouch for leaves the row ungated, and
 * the condition is left to be evaluated where it always was, at resolve time with a real context.
 * A false "available" costs the player one click into a no-op; a false "unavailable" hides a
 * capability with no way to discover it. Those are not symmetric, and this leans the safe way.
 *
 * Derived from {@link predicateActorRefFields} rather than a per-op switch
 * (`decisions/0183`): an op is decidable iff every actor ref it names is `"self"`, except the
 * context-fact ops below, which read something a bar enumeration never has however their refs
 * read. A new op therefore lands on the right side without an edit here, unless it reads a
 * context fact, in which case it joins that list.
 */
function selfDecidablePredicate(pred: Predicate): boolean {
  if (pred.op === "and" || pred.op === "or") return pred.preds.every(selfDecidablePredicate);
  if (pred.op === "not") return selfDecidablePredicate(pred.pred);
  if (readsContextFact(pred)) return false;
  return predicateActorRefFields(pred).every((f) => f.ref === "self");
}

/** Does `pred` read a fact that exists only at resolve time, whatever its actor refs say? */
function readsContextFact(pred: Predicate): boolean {
  switch (pred.op) {
    // The GM answers it during the resolution (a recorded `choices` entry), never at enumeration.
    case "gm_ruling":
    // The resolution-scoped `choose` fact ledger — it does not exist until the action resolves.
    case "chose":
      return true;
    // Compare `who` against the bound `attacker`/`target`, neither of which a bar binds.
    case "attacker_is":
    case "target_is":
      return true;
    // `carrierSource` is threaded per-clause by the trigger/modifier fire sites and is absent here.
    case "has_status":
      return pred.source === "carrier";
    default:
      return false;
  }
}

/**
 * Would firing this row right now do **nothing at all** because every one of its authored clauses
 * is gated out?
 *
 * `resolveActivate` runs each gathered clause and skips the ones whose `condition` is unmet,
 * warning `condition unmet — no effect` — so a row whose clauses are *all* gated out resolves to
 * zero steps. That is a fully accepted `request_activate` with no rejection and no state change:
 * the click looks like it worked. This mirrors that same per-clause verdict onto the descriptor so
 * the bar can grey the row instead of offering it.
 *
 * Deliberately `every`, not `some`: a multi-clause entry where one clause is live is still a row
 * worth firing. And a clause with no `condition` at all is unconditionally live, which short-
 * circuits the whole thing to `false` — the overwhelmingly common case.
 */
function conditionsAllUnmet(actions: readonly GatheredAction[], selfId: string, state: EncounterState | undefined): boolean {
  if (state === undefined || actions.length === 0) return false;
  return actions.every((a) => {
    const cond = a.effect.condition;
    if (!cond) return false;
    if (!selfDecidablePredicate(cond)) return false;
    return !evaluatePredicate(cond, { state, actors: { self: selfId } });
  });
}

/**
 * One enumerated action a combatant can take — the merged shape the bar reads. Core and granted
 * actions are the same shape; `source` distinguishes them and `route` says how it resolves.
 */
export type ActionDescriptor = {
  /** The action id: a core action id ({@link MECH_ACTIONS}'s `id`) or a granted composite `<itemId>:<actionIndex>`. */
  id: string;
  /** Core (universal) or loadout-granted. */
  source: ActionSource;
  /** Display name (core: the RAW name; granted: the action's OWN name when distinct, else the source item name). */
  name: string;
  /**
   * The **source** that granted this action — the equipment/talent/frame name — surfaced
   * in the row's ⓘ tooltip ("Granted by …"), NOT baked into {@link name}. Present only when the row's `name`
   * is the action's own distinct name and thus differs from its source (e.g. `name` "Smoke Grenade",
   * `sourceName` "Pattern-A Smoke Charges"). Absent on core rows and on granted rows whose name IS the source
   * (an action with no distinct own-name, an unnamed core bonus, or a missing-pack honest-degrade id).
   */
  sourceName?: string;
  /** The activation/economy label for display (core: Quick/Full/…; granted: the compendium action's `activation`). */
  cost: string;
  /** The per-row category chip (core: the RAW category; granted: `"GRANTED"`). */
  category: string;
  /** A `DEPLOYED` row's drone (`ActionEffect.usableBy`): the unit the activate names as its `target`. Absent on every other row. */
  carrierId?: string;
  /** A short blurb (core: the RAW blurb; granted: the compendium action's `detail`; absent when unknown). */
  blurb?: string;
  /** How this action resolves — the route the bar dispatches on. */
  route: MechActionRoute;
  /**
   * Whether this action resolves end-to-end today. **Core:** its engine path is live (`status === "live"`).
   * **Granted:** *derived* — an authored `action` body exists in the overlay for its id.
   * A non-`resolvable` granted action is the advisory "GM adjudicates" row; the bar must not fire it.
   */
  resolvable: boolean;
  /**
   * Whether every authored clause behind this row is currently **gated out by its own `condition`**,
   * so firing it would resolve to zero steps. Distinct from all three of its neighbours, and the
   * distinction is the point:
   *
   * - {@link resolvable}`=== false` means there is **no engine body** — the advisory "GM adjudicates"
   *   row. A conditioned row emphatically HAS a body; it just isn't armed yet.
   * - {@link usedUp} is the **frequency** wall (once-per-turn, or a scene/round `Limited N` cap).
   *   Orthogonal: a row can be freshly available for the turn and still gated out.
   * - The frontend's `statusBlock` is the **actor-status** wall (Stunned / Shut Down / Down and Out).
   *
   * The Everest's "Boost (Hyperspec Fuel Injector)" is the live carrier: its clause is conditioned
   * on `has_status "Power Up"`, so before the protocol runs this reads `true` and the bar renders a
   * greyed, non-fireable row saying so, instead of a live button whose click round-trips a fully
   * accepted `request_activate` that does nothing.
   *
   * **Always `false` when {@link EnumerateActionsArgs.state} is omitted** (nothing to evaluate
   * against) and for any condition not answerable from `self` alone — see
   * {@link selfDecidablePredicate} for why the undecidable case fails open rather than closed.
   * Core rows carry no authored condition, so theirs is always `false`.
   */
  conditionUnmet: boolean;
  /**
   * Whether this row's {@link cost} reads `"Free"` because a **held cost earmark rewrote it**, rather
   * than because the action is natively free.
   *
   * The distinction is not academic, and it exists because the bar cannot recover it from `cost`
   * alone: Overcharge's catalog cost is *already* `"Free"`, so a render site keying on
   * `cost === "Free"` would relabel Overcharge forever while saying nothing useful. Only the
   * registry holds both the catalog cost and the earmark, so it answers the question once here.
   * Rewriting `cost` alone is not enough either — no core-row render site reads `cost` at all (the
   * chip renders `category`, the badge renders {@link value}), so this is the field the bar reads to
   * actually show an earmarked row as free.
   *
   * Always `false` on granted rows — an earmark rewrites the CORE row's cost by design, never mints
   * a second free row beside it.
   */
  /**
   * Granted only: the overlay action is a **Quick Tech** (`activation: "quick_tech"`, Crush
   * Targeting), so a Full Tech may take it as a pick. Omitted for anything else.
   */
  quickTech?: true;
  costEarmarked: boolean;
  /** The UI targeting flow (self / single). Meaningful for resolvable actions; cosmetic for advisory ones. */
  targeting: ActionTargeting;
  /**
   * Optional base-derived **value badge** kind, carried through for **core** descriptors so the bar can read
   * the whole row off this one enumerated list — the bar resolves it to a display string via
   * `mechActionValueLabel(value, { speed })` (e.g. Boost's `SPD {speed}`) or, for an NPC descriptor,
   * `npcActionValueLabel(value, { tier, speed })` (NPC rows come from {@link NPC_ACTIONS}, whose own
   * `value` kinds — `"accuracy"` / `"improvised"` — this union widens to carry). Absent on granted
   * descriptors (a granted action carries no base-derived badge).
   */
  value?: MechActionValueKind | NpcActionValueKind;
  /**
   * Whether this id is currently unusable — either the once-per-turn wall or, for a granted action
   * whose item carries `Limited N` / an explicit RAW frequency, the coarser scene/round wall it froze
   * onto. `true` iff `EnumerateActionsArgs.budget` was supplied AND EITHER: `canUseAction`
   * (`@lancer-vtt/domain`) reads the combatant's per-turn `actionUses` ledger entry for this id as
   * exhausted against its resolved allowance (`combatant.base.actionAllowances[id] ?? 1`); OR
   * the id's frozen affordance record names a `"scene"`/`"round"`-grain `cap` rate or a `limited`
   * pool and the matching count (`EnumerateActionsArgs.roundUses[id]` /
   * `current.affordanceState[key]` for the rate, `current.affordanceState[<limited.pool ?? key>]`
   * for the pool) has reached its `max`. The two walls are independent — a granted action can be within its per-turn allowance and
   * STILL read `usedUp: true` because its Limited charges are gone for the rest of the scene (or vice
   * versa within the same turn); the per-turn ledger resets every turn, the scene charge count does
   * not. The bar greys out / blocks a row with `usedUp: true`, mirroring how it already blocks a row
   * with no action points left — this is a SEPARATE, orthogonal wall (a turn can have points left and
   * still be barred from repeating a specific action). Always `false` when `budget` is omitted (no
   * active-turn context to gate against — e.g. viewing a combatant that isn't mid-turn). Computed
   * uniformly for core AND granted ids.
   */
  usedUp: boolean;
  /**
   * The action's own RAW `range`, frozen through the SAME {@link parseRange} a weapon profile's range
   * goes through — so a granted action's reach reads with the identical `WeaponRange[]` vocabulary the
   * attack path already speaks.
   *
   * Load-bearing for the **cell-pick** rows: RAW's thrown deployables are ranged ("Throw a Smoke
   * Grenade **within Range 5**", "Throw a Frag Grenade within Range 5"). Without it on the descriptor
   * the browser has nothing to wash a reach overlay from and the resolve path has nothing to gate a
   * `site` against, so a thrown charge could land anywhere on the map.
   *
   * Absent when the source carries no range (a self/adjacent action, a deploy affordance minted off
   * `deployables[]`, a pilot-effect row) — absent means **ungated**, exactly as `attackRange === null`
   * already means ungated on the attack path.
   */
  range?: WeaponRange[];
  /**
   * The directed-deploy **pick shape** — present iff `targeting === "sites"`, in which case it is
   * ALWAYS the literal `{count, size}` (never `"advisory"`; `targetingFromOverlay` only returns
   * `"sites"` when {@link directedDeployRequirement} already resolved a literal requirement, so this
   * field's presence is a direct function of `targeting`, not an independent maybe). The board's
   * arming site (`CombatWindows.tsx`'s `fireGranted`) reads `count`/`size` off THIS field to mint the
   * draft (`armDirectedDeployDraft`) rather than re-deriving them client-side — {@link
   * directedDeployRequirement} stays the ONE place the shape is computed, this is just where its
   * result rides to the browser.
   */
  sites?: DirectedDeployRequirement;
};

/** The minimal compendium action shape the enumerator reads (rides through `.passthrough()`, so untyped). `attack` is `"tech"` for a RAW tech-attack action (Markerlight and the licensed family behind it) — otherwise absent. */
type RawCompendiumAction = { name?: unknown; activation?: unknown; detail?: unknown; range?: unknown; attack?: unknown };
/** The minimal compendium item shape: a name + its `actions[]` array (and a system's `deployables[]`). */
type RawActionItem = { name?: unknown; actions?: unknown; deployables?: unknown };
/** The minimal compendium deployable shape a deploy row is displayed from. */
type RawDeployable = { name?: unknown; activation?: unknown; detail?: unknown };

/**
 * The directed-deploy pick shape to stash on a `"sites"`-targeting descriptor — a second, tiny call to
 * {@link directedDeployRequirementFromActions} for its `{count, size}` half rather than reshaping
 * {@link targetingFromOverlay}'s return, so `directedDeployRequirementFromActions` stays the ONE place
 * a caller reads the pick shape (`targetingFromOverlay` itself already made the SAME call to decide
 * `"sites"` vs `"advisory"`; this just re-asks for the value half of that same deterministic read).
 * Every call site guards on `targeting === "sites"` BEFORE calling this (the `max-params`-3 rule pushed
 * the check out of the signature). Takes the SAME already-gathered `actions` list the caller's
 * `targetingFromOverlay` call gathered — `gatherActions({ownerId, entityId, overlay})` is deterministic
 * per id, so re-deriving it here for the SAME id would just re-walk it.
 */
function sitesRequirement(actions: readonly GatheredAction[], live?: DeployRequirementLiveness): DirectedDeployRequirement | undefined {
  const req = directedDeployRequirementFromActions(actions, live);
  // Callers only reach here when `targeting === "sites"`, which only happens when
  // `directedDeployRequirementFromActions` already returned a literal requirement (see
  // `targetingFromOverlay`), so `req` here is always the `{count,size}` object — the
  // `"advisory"`/`undefined` arms are unreachable in practice, kept only so this stays total.
  return req === "advisory" || req === undefined ? undefined : req;
}

/** Core targeting heuristic by route — attack/activate single-target, the rest self (cosmetic; core rows bind their own handler by `id`, not via `targeting`). */
function coreTargeting(route: MechActionRoute): ActionTargeting {
  return route === "attack" || route === "activate" ? "single" : "self";
}

/** Map a compendium `activation` string to a {@link MechActionRoute}: reactions are Category B, everything else activates. */
function routeForActivation(activation: string | undefined): MechActionRoute {
  return (activation ?? "").trim().toLowerCase() === "reaction" ? "reaction" : "activate";
}

/**
 * The UI targeting flow the overlay's authored `action` body for `id` needs. Inspects the gathered
 * action atoms, most-specific first:
 * - a `deploy_object`/`deploy_mine` atom (directed placement; the mine's fixed 1-pick case) ⇒
 *   `"sites"`/`"advisory"` per {@link directedDeployRequirement}. Checked FIRST — neither atom carries
 *   a `for_each origin:"site"` selector nor a `target` ref, so it would otherwise fall through to
 *   `"self"` below.
 * - a site-origin area atom — a `for_each`, `choose_random` or `choose_from_area` selector, or a
 *   `create_area`, reading `origin: "site"` (a point-blanket area action — HEX Charges' per-target
 *   `for_each`, Smoke Charges' standing `create_area`) ⇒ `"cell"`. Checked before the `target`-ref
 *   scan because those atoms bind their `target` INSIDE the selector (and `create_area` carries no
 *   `target` ref at all), either of which would otherwise read as a single-token action below — the
 *   board pick is a CELL (the template center), not one token. **Unless a `move_self_toward_target`
 *   comes first in the same body**: the move sets the site to its endpoint (`ctx.site`), so the
 *   selector reads where the mover landed and a picked cell would be overwritten unread (Impale,
 *   Avalanche Charge). Such a body falls through to the scan below, which reads it as `"self"`:
 *   the direction-first flow, with the move's own direction prompt.
 * - otherwise an atom binding the `target` actor ref, as its subject or as a status `partner` (a
 *   single-target action) ⇒ `"single"`.
 * - else `"self"`.
 * Called only when the id is resolvable (it has gathered actions). Takes the already-gathered
 * `actions` list — every call site already ran `gatherActions` for this same `(ownerId, id, overlay)`
 * to compute `resolvable`, so this reuses that walk instead of re-gathering it here AND again inside
 * {@link directedDeployRequirementFromActions}.
 */
/** The selector atoms whose `in` is an `AreaSelector` enumerated around `origin`. */
const SITE_SELECTOR_OPS: ReadonlySet<unknown> = new Set(["for_each", "choose_random", "choose_from_area"]);

function targetingFromOverlay(actions: readonly GatheredAction[], live?: DeployRequirementLiveness): ActionTargeting {
  const deployRequirement = directedDeployRequirementFromActions(actions, live);
  if (deployRequirement === "advisory") return "advisory";
  if (deployRequirement !== undefined) return "sites";
  for (const a of actions) {
    for (const atom of a.effect.effect) {
      const node = atom as { op?: unknown; in?: { origin?: unknown }; origin?: unknown };
      if (node.op === "move_self_toward_target") break; // every later site-origin read is the endpoint.
      if (SITE_SELECTOR_OPS.has(node.op) && node.in?.origin === "site") return "cell";
      if (node.op === "create_area" && node.origin === "site") return "cell";
    }
  }
  for (const a of actions) {
    for (const atom of a.effect.effect) {
      const refs = atom as { target?: unknown; actor?: unknown; away_from?: unknown; partner?: unknown };
      if (refs.target === "target" || refs.actor === "target" || refs.away_from === "target" || refs.partner === "target") return "single";
    }
  }
  return "self";
}

/**
 * Route-specific `resolvable` for a **tech-attack** granted row. The ordinary derivation
 * (`gathered.length > 0`, off {@link gatherActions}'s `kind:"action"` lookup) is the WRONG question
 * here: a tech attack's authored consequence — 2 heat + Lock On for Markerlight — lives as a
 * `kind:"triggered", on:"attack"` clause on the carrier's BARE overlay entry (the same entry
 * `gatherHandlers`/`SYSTEM_VECTOR` reach it by), not a `kind:"action"` clause. The tech attack itself
 * fires through `request_attack` (routed `"attack"` here), never `request_activate` — there is no
 * activate-shaped body to author, so `gatherActions` would read `resolvable: false` FOREVER. This
 * reads the bare `itemId`'s overlay entry directly instead.
 *
 * **Answers a different question from the wall on purpose**: this says "can we mechanize it yet"
 * (gap-honesty); `legalTechAttackWeaponIds` says "does this combatant legitimately carry this tech
 * attack" (anti-forgery, off the frozen `base.techAttackActions`, compendium-free). An unauthored
 * carrier reads `resolvable: false` here while the wall still admits it — that divergence is
 * deliberate, not a bug.
 */
function techAttackResolvable(itemId: string, overlay: LoadedOverlay): boolean {
  const entry = overlay[itemId];
  if (!entry) return false;
  return entry.effects.some((e) => e.kind === "triggered" && e.on === "attack");
}

/**
 * Item ids whose **entire** `actions[]` array is a GRANTED-row duplicate of an already-live,
 * always-co-gated **core** row. `ms_comp_con_class_assistant_unit`'s real compendium `actions[0]`
 * ("Engage COMP/CON") names the exact same
 * protocol the core `engage_compcon` row (`mechActions.ts`'s `MECH_ACTIONS`, `route: "reducer"`,
 * wired end to end to `request_engage_compcon`) already renders for any carrier — `hasCompConSystem`
 * gates BOTH the core row's visibility (`CombatWindows.tsx`) and `checkEngageCompConLegality`, so
 * the two rows always co-occur for the same combatant, never independently. The granted copy's
 * overlay ships `effects: []` (nothing RAW-specific left to gate — see `git show 4a83ea50:packs/core-rulebook/entries/systems/ms_comp_con_class_assistant_unit/candidate.json`), so
 * pressing it can never resolve to anything: a permanently inert row beside the one that works.
 * Suppressed here, at the composite-id `itemId`, NOT by the shared `tg_ai`+`tg_no_cascade` tag pair
 * `hasCompConSystem` itself gates on — that pair also marks `wm_uncle_class_comp_con` (a weapon mod
 * granting a genuinely DIFFERENT "UNCLE" free-action attack, with no core-row counterpart), which
 * must keep minting normally. A tag-based suppression would silently swallow that unrelated,
 * legitimate row too.
 */
const CANONICAL_DUPLICATE_GRANTED_ITEM_IDS = new Set(["ms_comp_con_class_assistant_unit"]);

/**
 * Read a granted action off the compendium by its composite id, in lookup order: system, then weapon,
 * then `npc_feature`, then `pilot_gear`. All four carry the SAME `actions[]` shape — an NPC's frozen
 * frozen granted composites point at `npc_feature` items, a pilot's (minted by
 * `freezePilotGearActionIds`, `spawn/pilot.ts`) at `pilot_gear` items — so the extra buckets are a pure
 * lookup-order extension, no new parse/shape. An id that resolves in none of them falls through to the
 * caller's honest-degrade fallback, which renders the raw composite id as `name` and a bare
 * `"Granted"` as `cost`. `null` if the pack isn't loaded (honest degrade).
 */
function lookupGranted(id: string, compendium: Compendium): { itemName: string; action: RawCompendiumAction } | null {
  const parsed = parseGrantedId(id);
  if (!parsed) return null;
  const item = (compendium.getById("system", parsed.itemId) ??
    weaponActionBearer(compendium.getById("weapon", parsed.itemId) as RawActionBearer | undefined) ??
    compendium.getById("npc_feature", parsed.itemId) ??
    compendium.getById("pilot_gear", parsed.itemId)) as RawActionItem | undefined;
  if (!item || !Array.isArray(item.actions)) return null;
  const action = item.actions[parsed.index] as RawCompendiumAction | undefined;
  if (!action || typeof action !== "object") return null;
  const itemName = typeof item.name === "string" ? item.name : parsed.itemId;
  return { itemName, action };
}

/**
 * Is `affordance` a compendium "Invade"-activation action (`ms_neurospike:0`) whose item's overlay
 * entry authors `invade_option` clauses? Those options are offered on the Quick and Full Tech cards'
 * "Invade ›" rows (`gatherInvadeOptions`), so the bar drops the action's own row, which would only
 * be an unauthored advisory duplicate.
 */
function offeredAsInvadeOption(affordance: Affordance, compendium: Compendium, overlay: LoadedOverlay): boolean {
  const { source } = affordance;
  if (source.kind !== "granted") return false;
  if (lookupGranted(affordance.key, compendium)?.action.activation !== "Invade") return false;
  return overlay[source.itemId]?.effects.some((e) => e.kind === "invade_option") ?? false;
}

/**
 * Is `affordance` a compendium "Reaction" action (`ms_stasis_bolt:1`, Interdiction Point) that the
 * item's overlay answers as an offered prompt, an optional `triggered` clause of the same `name`,
 * rather than as an action clause? The reaction reaches its owner as that prompt when it triggers, so
 * the bar drops the row, which could only render as an unresolvable reaction
 * (`ll1-granted-reactions` 11.1).
 */
function offeredAsReactionPrompt(affordance: Affordance, compendium: Compendium, overlay: LoadedOverlay): boolean {
  const { source } = affordance;
  if (source.kind !== "granted") return false;
  const action = lookupGranted(affordance.key, compendium)?.action;
  if (action?.activation !== "Reaction" || typeof action.name !== "string") return false;
  if (gatherActions({ ownerId: "", entityId: affordance.key, overlay }).length > 0) return false;
  return overlay[source.itemId]?.effects.some((e) => e.kind === "triggered" && e.optional === true && e.name === action.name) ?? false;
}

/**
 * Read a **deploy affordance**'s display name/cost/blurb off the compendium by its composite id —
 * the `deployables[]` counterpart to {@link lookupGranted}. `freezeActionIds` mints
 * `<itemId>:<actions.length + deployableIndex>` — `:N` is a position in the item's UNIFIED
 * affordance space (`actions[]` first, then `deployables[]`), not a bare `deployables[]` index — so
 * this subtracts the item's own `actions.length` to recover `deployableIndex`. The offset is
 * **derived here, from the same compendium item, never stored or guessed**; a deploy-only system
 * (Jericho, turret) has `actions.length === 0`, so its offset is 0.
 *
 * Checks `pilot_gear` beside `system` — a `deployables[]`-offset id (`pg_thermite_charge:0`) minted
 * by `freezePilotGearActionIds` (`spawn.ts`) is the identical shape one level down the multi-bucket
 * chain {@link lookupGranted} already uses; a `system` id and a `pilot_gear` id never collide
 * (distinct compendium categories), so the null-coalescing lookup order is safe the same way
 * `lookupGranted`'s is. `npc_feature` likewise: `spawnNpc` mints the same offset ids for an NPC
 * feature's `deployables[]` (Razor Swarms). `null` when the id isn't a system/pilot-gear deployable (a normal granted
 * action, a missing pack, or an out-of-range index).
 */
function lookupDeployable(id: string, compendium: Compendium): { itemName: string; deployable: RawDeployable } | null {
  const parsed = parseGrantedId(id);
  if (!parsed) return null;
  const item = (compendium.getById("system", parsed.itemId) ??
    compendium.getById("pilot_gear", parsed.itemId) ??
    compendium.getById("npc_feature", parsed.itemId)) as RawActionItem | undefined;
  if (!item || !Array.isArray(item.deployables)) return null;
  const actionCount = Array.isArray(item.actions) ? item.actions.length : 0;
  const deployableIndex = parsed.index - actionCount;
  const deployable = item.deployables[deployableIndex] as RawDeployable | undefined;
  if (deployableIndex < 0 || !deployable || typeof deployable !== "object") return null;
  const itemName = typeof item.name === "string" ? item.name : parsed.itemId;
  return { itemName, deployable };
}

/**
 * Read a granted-action row's NAME off the compendium when NEITHER {@link lookupGranted} NOR
 * {@link lookupDeployable} can — the compendium record carries NEITHER `actions[]` (nothing for
 * `lookupGranted` to index) NOR `deployables[]` (nothing for `lookupDeployable` to index either).
 * That is exactly the shape `enumerateActions`' enumerate-time overlay union exists to reach: a
 * system whose only authored granted-action body lives in the overlay, with nothing mirrored into
 * `lancer-data` at all (`ms_custom_paint_job`'s Custom Paint Job — a reaction, zero `actions[]`, zero
 * `deployables[]`). `freezeActionIds` (`spawn.ts`) never mints an id for such an item — it walks
 * `actions[]`/`deployables[]` to decide what to mint — so only the union reaches this. Returns the
 * item's own bare `.name`, the closest RAW-faithful label available, since there is no per-action name
 * to prefer the way `lookupGranted` prefers `action.name`. `null` when the item itself doesn't resolve
 * (a missing pack — the caller falls through to the raw-id degrade) OR *does* carry one of the two
 * arrays (a shape `lookupGranted`/`lookupDeployable` already own — this function must never shadow
 * them, including the out-of-range-index case where both already correctly fail closed). Systems and
 * pilot gear, matching the union's own scope (`base.equippedSystems` and `base.pilotGearEffectIds`;
 * Mobility Hardsuit's Fly is the pilot-gear case).
 */
function lookupOverlayOnlyItem(id: string, compendium: Compendium): { itemName: string } | null {
  const parsed = parseGrantedId(id);
  if (!parsed) return null;
  const item = (compendium.getById("system", parsed.itemId) ?? compendium.getById("pilot_gear", parsed.itemId)) as RawActionItem | undefined;
  if (!item) return null;
  if (Array.isArray(item.actions) || Array.isArray(item.deployables)) return null; // owned by lookupGranted/lookupDeployable
  const itemName = typeof item.name === "string" ? item.name : parsed.itemId;
  return { itemName };
}

/**
 * Read a pilot-effect id's display name/blurb off the compendium — the `talent`/`core_bonus`
 * counterpart to {@link lookupGranted} (which expects a compendium item's `.actions[]`, a shape
 * that does NOT apply here: `pilotEffectIds` entries are bare overlay keys, not composite ids
 * into an item's action array). `freezePilotEffectIds` (`spawn.ts`) emits `${talentId}:${rank}`
 * for a talent rank (the `:N` suffix is a RANK, never an action index) or a bare core-bonus id
 * with no suffix at all. Mirrors the display pattern the roster UI already uses
 * (`unitBuildView.ts`, `compute.ts`'s `collectPilotBonuses`):
 * the rank's own name as the row `name` with the talent as `sourceName` (e.g. name "Stable, Steady",
 * sourceName "Crack Shot"), or the core bonus's name (no distinct source — the bonus IS its own name).
 * Honest degrade to the bare id when neither category resolves it (a missing pack).
 */
function pilotEffectDisplay(id: string, compendium: Compendium): { name: string; sourceName?: string; blurb?: string } {
  const colon = id.lastIndexOf(":");
  if (colon > 0 && colon < id.length - 1) {
    const rank = Number(id.slice(colon + 1));
    if (Number.isInteger(rank) && rank > 0) {
      const talentId = id.slice(0, colon);
      const talent = compendium.getById("talent", talentId);
      if (talent) {
        const rankEntry = talent.ranks[rank - 1] as { name?: string; description?: string } | undefined;
        const rankName = typeof rankEntry?.name === "string" ? rankEntry.name : undefined;
        return {
          name: rankName ?? talent.name,
          ...(rankName ? { sourceName: talent.name } : {}),
          ...(typeof rankEntry?.description === "string" ? { blurb: rankEntry.description } : {}),
        };
      }
    }
  }
  const bonus = compendium.getById("core_bonus", id);
  if (bonus) {
    return { name: bonus.name, ...(bonus.description !== undefined ? { blurb: bonus.description } : {}) };
  }
  // An NPC's bare feature id (`base.npcFeatureEffectIds`) reaches this same union.
  const feature = compendium.getById("npc_feature", id);
  if (feature) return { name: feature.name };
  return { name: id };
}

/**
 * Read a frame-trait/core-system id's display name/blurb off the compendium — the frame-effect
 * counterpart to {@link pilotEffectDisplay}. `freezeFrameTraitEffectIds` (`spawn.ts`) SYNTHESIZES
 * these ids (traits/core-systems carry no native id), so there's no direct lookup; instead this scans
 * every loaded frame's trait + core-system entries ({@link frameEffectEntries}, the SAME derivation
 * the freeze itself uses, so the two can never name an entry differently) for the one whose
 * synthesized id matches, surfacing the trait/core-system's own name as the row `name` with the frame
 * as `sourceName` (e.g. name "Initiative", sourceName "Everest"), mirroring the talent-rank split.
 * Honest degrade to the bare id when no loaded frame's trait/core-system resolves it (a missing pack,
 * or a stale id from a since-edited frame).
 */
function frameEffectDisplay(id: string, compendium: Compendium): { name: string; sourceName?: string; blurb?: string } {
  for (const frame of compendium.list("frame")) {
    const entry = frameEffectEntries(frame as unknown as Record<string, unknown>).find((e) => e.id === id);
    if (entry) return { name: entry.name, sourceName: frame.name, ...(entry.description !== undefined ? { blurb: entry.description } : {}) };
  }
  return { name: id };
}

/** Capitalize a gathered action's `activation` for display (`MechActionCost`'s RAW labels: Quick/Full/Protocol/Reaction/Free). */
function activationCost(activation: string): string {
  return activation.length > 0 ? activation[0]!.toUpperCase() + activation.slice(1) : activation;
}

export type EnumerateActionsArgs = {
  /** The combatant whose actions to enumerate (`carriedAffordances` over its frozen `base.affordances` supplies the granted set). */
  combatant: Combatant;
  /** The compendium the granted actions are displayed from (the client holds it; ids-only freeze, display by id). */
  compendium: Compendium;
  /** The overlay `resolvable` is derived against — defaults to the single global {@link SEED_OVERLAY} (no per-combatant merge). */
  overlay?: LoadedOverlay;
  /**
   * The acting combatant's current per-turn budget — supplied ONLY when `combatant` is mid-turn (the
   * caller reads `state.activation.turn.budget` when `turn.phase === "acting" && turn.actor ===
   * combatant.id`). Drives each descriptor's `usedUp`. Omit when there's no active-turn context (a
   * non-acting combatant, or outside combat) — every descriptor then reads `usedUp: false`.
   */
  budget?: TurnBudget;
  /**
   * The acting combatant's current-round action-use ledger (`ActivationState.actionRoundUses[id]`) —
   * the `"round"`-grain sibling of `budget`, supplied under the same mid-turn condition (the caller
   * reads `state.activation.actionRoundUses[combatant.id]`). Drives `usedUp` for a granted action
   * whose frozen affordance `cap.grain === "round"`; unused when `budget` is omitted (every
   * descriptor already reads `usedUp: false` in that case) or when no id carries a round-grain cap
   * (today's only frozen coarser grain is `"scene"` — Smoke/Frag/HEX Charges' `Limited N`; `"round"`
   * is a real, reachable RAW frequency this plumbs for free).
   */
  roundUses?: Record<string, number>;
  /**
   * The encounter the combatant is standing in — read ONLY to answer each granted row's authored
   * clause `condition` for {@link ActionDescriptor.conditionUnmet}. Every enumeration site that
   * renders a bar has this in hand already.
   *
   * Optional, and its absence degrades **open**: every row reads `conditionUnmet: false`. That suits
   * a caller doing a pure catalog read with no board in scope, but it does mean a *rendering* caller
   * that forgets to pass it silently loses the gate — so if you are adding a call site that feeds a
   * UI, pass it.
   */
  state?: EncounterState;
};

/**
 * Enumerate the action descriptors a combatant can take — the one list the bar reads:
 * **core** ({@link MECH_ACTIONS}, each tagged with its route) ∪ **granted** (`carriedAffordances`, displayed off
 * the compendium, `resolvable` derived against the overlay) ∪ the pilot/frame effects' actions ∪ the
 * actions its own statuses author. The single seam; pure.
 */
export function enumerateActions(args: EnumerateActionsArgs): ActionDescriptor[] {
  const { combatant, compendium, overlay = SEED_OVERLAY, budget, roundUses, state } = args;
  const live: DeployRequirementLiveness | undefined = state ? { state, ownerId: combatant.id } : undefined;

  /** Is every authored clause behind this row gated out right now? See {@link conditionsAllUnmet}. */
  const conditionUnmet = (gathered: readonly GatheredAction[]): boolean => conditionsAllUnmet(gathered, combatant.id, state);

  // The once-per-turn ledger check reuses the SAME `canUseAction` predicate the reducer (Boost) and
  // the room (everything else) gate on, so the bar's affordance never drifts from the actual wall.
  // `false` with no `budget` supplied.
  //
  // PLUS the coarser scene/round wall a `Limited N` (or explicit RAW-frequency) granted action
  // freezes onto its affordance record's `cap`. That wall is INDEPENDENT of the per-turn ledger:
  // `actionUses` resets to `{}` every turn, but a scene-grain cap's charge count
  // (`current.affordanceState[<key>].uses`) does not — so on a fresh turn `canUseAction` alone would
  // read `false` (nothing used up THIS turn) even though every Limited charge is already spent for
  // the rest of the encounter and the server would hard-reject the activate. Reuses
  // `checkActionAvailable`'s exact per-grain predicates (`canUseSceneAction`/`canUseRoundAction`,
  // `economy.ts`'s "one door" rule) so this affordance can't drift from the real wall. Both checks
  // are gated on `budget !== undefined` (no active-turn context ⇒ every id reads `usedUp: false`) —
  // the scene/round state itself doesn't depend on whose turn it is, but the descriptor only surfaces
  // the block while inspecting the combatant mid-turn, mirroring the per-turn check's own scope.
  /**
   * Does this combatant hold a cost earmark for the core action `id`? `id` is a bare core-action id,
   * which is the same string space `EARMARK_VERBS` names for every core verb — the divergence to
   * guard against is between the earmark verb and the once-per-turn LEDGER id, and the ledger only
   * collapses on the ACTIVATE/attack routes, never on a bare core row like `boost`. A core id that is
   * not an earmark verb (`overcharge`, `hide`, …) simply never matches.
   */
  const earmarkedFree = (id: string): boolean =>
    isEarmarkVerb(id) && freeActionUses(combatant.current.resources, id) > 0;

  /**
   * `record` is passed in by the GRANTED rows, which hold the affordance record already; every
   * other caller (the core rows) omits it and takes the exact frozen-table lookup, bit-for-bit what
   * the deleted `base.actionUsageCaps?.[id]` raw read produced.
   *
   * The granted rows must NOT fall back to that lookup: some composites exist only as the
   * enumerate-time overlay mint and are never frozen, so the record in hand is the only place
   * their cap lives.
   */
  const usedUp = (id: string, record?: Affordance): boolean => {
    if (budget === undefined) return false;
    // The allowance is the frozen base PLUS any runtime grant, matching the reducer's own Boost
    // wall and `checkActionUseLegality`'s. Reading only the frozen half greys out a row the server
    // would still accept — a triggered `grant_action_allowance` (Everest's free Boost) lands in
    // `current.resources`, which this affordance must see.
    const affordance = record ?? findAffordance(combatant.base.affordances, id);
    const frozen = affordance?.cap;
    // A Protocol never rides `actionUses`: it reads the protocol-safe `protocolUses` ledger, once
    // per turn unless a `turn`-grain cap says otherwise — the same wall `checkGrantedActionLegality`
    // enforces (RAW Core p.62). Reading `actionUses` here would leave a spent Protocol row live.
    if (isProtocolAction(combatant.id, id, overlay)) {
      if (!canUseProtocolAction(budget, id, frozen?.grain === "turn" ? frozen.max : 1)) return true;
    } else {
      const allowance = (combatant.base.actionAllowances?.[id] ?? 1) + grantedActionAllowanceBonus(combatant.current.resources, id);
      if (!canUseAction(budget, id, allowance)) return true;
    }
    // Every fact present spends (`decisions/0202`), so either one exhausted greys the row: the rate
    // on `id`, and the Limited pool on `pool ?? id` — the ledger slot every affordance minted off
    // one item shares, exactly as `checkGrantedActionLegality` reads it. Reading `id` alone for the
    // pool would show a drained Smoke-Charges mine row as still available.
    if (frozen?.grain === "scene" && !canUseSceneAction(combatant.current.affordanceState, id, frozen.max)) return true;
    if (frozen?.grain === "round" && !canUseRoundAction(roundUses, id, frozen.max)) return true;
    const limited = affordance?.limited;
    return limited !== undefined && !canUseSceneAction(combatant.current.affordanceState, limited.pool ?? id, limited.max);
  };

  // Core source = the combatant's OWN frozen `base.coreActionIds`, resolved against the global
  // catalog — with NO read of the combatant's kind field. The spawn adapter assembled this
  // composite-id set at spawn (a mech/drone/object → `mech:*` ids, an NPC → `npc:*`, a pilot →
  // `pilot:*`); the registry just reads whatever was frozen. A new acting-kind therefore costs only
  // its spawn adapter — this reader is untouched.
  //
  // `coreDescriptor` is the ONE transform: it emits the resolved action's **bare** `id` on the
  // outward descriptor (composite-internal, bare-external), so every downstream consumer that keys on
  // the bare id — the budget ledger, reaction tracking, `apply_resolved` batches, the frontend
  // aim-mode — reads one stable string. `actionRegistry.test.ts` pins the mech row set against it.
  const coreDescriptor = (a: CoreAction): ActionDescriptor => ({
    id: a.id,
    source: "core",
    name: a.name,
    // The core row's cost REFLECTS a held cost earmark: the override rewrites this row rather than
    // minting a second "free" row beside it, which is why the Everest ends up with ONE Boost
    // affordance whose cost reads Free while the trait is live. Reads through `freeActionUses` —
    // the same door the pricing
    // and consumption sites read — so the badge cannot drift from what will actually be charged.
    cost: earmarkedFree(a.id) ? "Free" : a.cost,
    // The bar cannot re-derive this from `cost` (Overcharge is natively "Free") — see the field's doc.
    costEarmarked: earmarkedFree(a.id),
    category: a.category,
    blurb: a.blurb,
    route: a.route,
    resolvable: a.status === "live",
    // A core action is a RAW verb resolved by an engine path, not an authored overlay clause — there
    // is no `condition` on it to gate. The Everest's free Boost reaches the CORE Boost row through
    // the earmark's `cost` rewrite above, never through a clause.
    conditionUnmet: false,
    targeting: coreTargeting(a.route),
    usedUp: usedUp(a.id),
    // Carry the core action's base-derived value-badge kind (e.g. Boost's `speed`, Grapple's `accuracy`)
    // so the bar resolves the whole row off this one list. Absent ⇒ no badge.
    ...(a.value !== undefined ? { value: a.value } : {}),
  });

  // Resolve each frozen composite → its catalog definition → a bare-id descriptor, in the frozen
  // order. The unique-bare-id invariant — because the outward descriptor id is BARE and the
  // ledger/reaction/command/aim-mode consumers key per-combatant on that bare string, a combatant
  // whose frozen set resolved two composites to the SAME bare id would silently conflate them — and
  // the unknown/off-shape-composite honest-degrade both live in {@link resolvedCoreActions}
  // (`./economy`), so the bar (here) and the carrier-ownership wall's `carriedCoreActionIds` consume
  // ONE resolution, never two restatements.
  const core: ActionDescriptor[] = resolvedCoreActions(combatant).map(({ action }) => coreDescriptor(action));

  // The **carried granted affordance records** — the frozen table UNION the enumerate-time overlay
  // mint, with a destroyed system's actions already excluded (Core p.80 System Trauma).
  //
  // **`base.affordances` alone would be wrong here, and that is why this goes through
  // `carriedAffordances`.** Some composites exist ONLY as the mint — a system whose overlay
  // authors a `kind:"action"` clause but whose compendium record carries neither `actions[]` nor
  // `deployables[]`, and whose clause carries no `limitedUses` for the spawn's overlay-cap arm to
  // freeze either (`ms_type_i_flight_system:0`). Reading the frozen list directly would silently
  // drop exactly those rows off the bar.
  //
  // Lives in `carriedAffordances` (`./economy`) so the bar (here) and the `request_activate`
  // legality wall (`legalActivateActionIds`, same module) consume ONE derivation, never two
  // restatements — see that function's own doc for why the destroyed-system filter lives there and
  // NOT the canonical-duplicate suppression below (a display-only dedup, not a possession question).
  //
  // Each row carries its own `cap` inline, so `usedUp` needs no second lookup — and for a
  // mint-only row there IS no frozen entry to look up.
  const carried = carriedAffordances(combatant, overlay);

  const granted: ActionDescriptor[] = carried
    // Suppress the canonical-duplicate granted row (see the const's own doc) — stays here, not in
    // the shared derivation, because it hides an inert duplicate ROW, not a fact about what the
    // combatant carries.
    .filter((a) => !(a.source.kind === "granted" && CANONICAL_DUPLICATE_GRANTED_ITEM_IDS.has(a.source.itemId)))
    .filter((a) => !offeredAsInvadeOption(a, compendium, overlay))
    .filter((a) => !offeredAsReactionPrompt(a, compendium, overlay))
    .map((affordance) => {
      const id = affordance.key;
      // `resolvable` is DERIVED against the single global overlay — never frozen.
      const gathered = gatherActions({ ownerId: combatant.id, entityId: id, overlay });
      const resolvable = gathered.length > 0;
      const found = lookupGranted(id, compendium);
      if (!found) {
        // A **deploy affordance** minted off a system's `deployables[]` — the id
        // parses like a granted action but points at `deployables[index]`, not `actions[index]`, so
        // `lookupGranted` missed. Display the deployable's own name + cost; `resolvable` is derived
        // the same way (the authored deploy overlay clause via the bare-id fallback). Cost reads the
        // authored clause's activation when resolvable (a `deployables[]` entry's own `activation` is
        // often "None" while the deploy clause is Quick); an unauthored deploy row falls back to the
        // RAW deployable activation, then "Deploy".
        const deploy = lookupDeployable(id, compendium);
        if (deploy) {
          const { itemName, deployable } = deploy;
          const deployName = typeof deployable.name === "string" && deployable.name.trim() ? deployable.name.trim() : undefined;
          const clauseActivation = gathered[0]?.effect.activation;
          const rawActivation = typeof deployable.activation === "string" && deployable.activation.trim() ? deployable.activation.trim() : undefined;
          const cost = clauseActivation ? activationCost(clauseActivation) : (rawActivation ?? "Deploy");
          const detail = typeof deployable.detail === "string" ? deployable.detail : undefined;
          const deployTargeting = resolvable ? targetingFromOverlay(gathered, live) : "self";
          const deploySites = deployTargeting === "sites" ? sitesRequirement(gathered, live) : undefined;
          return {
            id,
            source: "granted",
            name: deployName ?? itemName,
            ...(deployName && deployName !== itemName ? { sourceName: itemName } : {}),
            cost,
            category: "GRANTED",
            ...(detail !== undefined ? { blurb: detail } : {}),
            route: "activate",
            resolvable,
            conditionUnmet: conditionUnmet(gathered),
            costEarmarked: false,
            targeting: deployTargeting,
            usedUp: usedUp(id, affordance),
            ...(deploySites ? { sites: deploySites } : {}),
          } satisfies ActionDescriptor;
        }
        // An id the enumerate-time union above minted off the overlay's OWN authored clause count —
        // the compendium record carries NEITHER `actions[]` (so `lookupGranted` above found nothing
        // to index) NOR `deployables[]` (so `lookupDeployable` just above found nothing either).
        // Reuses `lookupDeployable`'s cost-derivation pattern (`activationCost` off the authored
        // clause's `activation`) since there is no compendium `action.activation` to read for this
        // shape either — the two rows share the identical "no compendium body, only an overlay body"
        // structure. `resolvable` is guarded explicitly (rather than assumed) so a caller that
        // somehow reaches this id without the clause actually resolving degrades to the honest
        // raw-id fallback below instead of fabricating a name for a non-resolving row.
        const overlayOnly = resolvable ? lookupOverlayOnlyItem(id, compendium) : null;
        if (overlayOnly) {
          const { itemName } = overlayOnly;
          const clauseActivation = gathered[0]?.effect.activation;
          const cost = clauseActivation ? activationCost(clauseActivation) : "Granted";
          const overlayTargeting = targetingFromOverlay(gathered, live);
          const overlaySites = overlayTargeting === "sites" ? sitesRequirement(gathered, live) : undefined;
          // NOT a tech-attack site: reached only when the compendium item carries NEITHER
          // `actions[]` NOR `deployables[]` (see this branch's own doc above) — a RAW tech attack
          // is BY DEFINITION an `actions[]` entry with `attack:"tech"`, so an id that lands here
          // structurally cannot be one (it would have resolved via `found`/`lookupGranted` above
          // instead, which DOES carry the tech-attack branch).
          return {
            id,
            source: "granted",
            name: itemName,
            cost,
            category: "GRANTED",
            route: routeForActivation(clauseActivation),
            resolvable,
            conditionUnmet: conditionUnmet(gathered),
            costEarmarked: false,
            targeting: overlayTargeting,
            usedUp: usedUp(id, affordance),
            ...(overlaySites ? { sites: overlaySites } : {}),
          } satisfies ActionDescriptor;
        }
        // Missing pack (a spectator without this player's content): honest fallback — render the id, no crash.
        const fallbackTargeting = resolvable ? targetingFromOverlay(gathered, live) : "self";
        const fallbackSites = fallbackTargeting === "sites" ? sitesRequirement(gathered, live) : undefined;
        return {
          id,
          source: "granted",
          name: id,
          cost: "Granted",
          category: "GRANTED",
          route: "activate",
          resolvable,
          conditionUnmet: conditionUnmet(gathered),
          costEarmarked: false,
          targeting: fallbackTargeting,
          usedUp: usedUp(id, affordance),
          ...(fallbackSites ? { sites: fallbackSites } : {}),
        } satisfies ActionDescriptor;
      }
      const { itemName, action } = found;
      const actionName = typeof action.name === "string" && action.name.trim() ? action.name.trim() : undefined;
      const activation = typeof action.activation === "string" ? action.activation : undefined;
      const detail = typeof action.detail === "string" ? action.detail : undefined;
      // The action's own RAW reach, through the same parser weapon ranges use. Empty ⇒ omit the
      // field entirely (absent means ungated, never `[]` — a caller checking `range?.length` and one
      // checking `range !== undefined` must agree).
      const range = parseRange(action.range as RawWeapon["range"]);
      // A RAW **tech-attack** action (`isTechAttackAction`: `attack: "tech"` on the action, or nested in its hit effect — Markerlight and the licensed
      // family behind it) routes `"attack"`, not `"activate"`, read off the LIVE COMPENDIUM action
      // this branch already resolved (`found`/`lookupGranted`) — deliberately NOT
      // `base.techAttackActions` (the frozen wall fact). The registry and the wall answer different
      // questions and are ALLOWED to differ: the registry asks "can we mechanize it yet"
      // (gap-honesty, compendium-backed), the wall asks "does this combatant legitimately carry this
      // tech attack" (anti-forgery, compendium-free).
      const isTechAttack = isTechAttackAction(action);
      // Route-specific `resolvable`: the ordinary `gathered.length > 0` (an `action`-kind overlay
      // clause) is the wrong question for a tech attack, whose authored consequence — if any — lives
      // as a `triggered, on:"attack"` clause on the carrier's BARE overlay entry, not an
      // `action`-kind one (see {@link techAttackResolvable}'s own doc). Preserves the advisory
      // degrade: an unauthored carrier (Markerlight ships `{"effects": []}`) reads
      // `resolvable: false`, and flips `true` the day a body is authored, with no change here.
      const techParsed = isTechAttack ? parseGrantedId(id) : null;
      const foundResolvable = isTechAttack ? (techParsed ? techAttackResolvable(techParsed.itemId, overlay) : false) : resolvable;
      // A tech attack always targets one board token (Sensors range vs. LoS, never a cell/area
      // pick) — skip the overlay-derived `targetingFromOverlay` classification entirely; that
      // heuristic reads the (irrelevant, and for Markerlight today empty) `action`-kind atoms.
      const foundTargeting = isTechAttack ? "single" : resolvable ? targetingFromOverlay(gathered, live) : "self";
      const foundSites = foundTargeting === "sites" ? sitesRequirement(gathered, live) : undefined;
      return {
        id,
        source: "granted",
        name: actionName ?? itemName,
        ...(actionName ? { sourceName: itemName } : {}),
        cost: activation ?? "Granted",
        category: "GRANTED",
        ...(detail !== undefined ? { blurb: detail } : {}),
        route: isTechAttack ? "attack" : routeForActivation(activation),
        resolvable: foundResolvable,
        conditionUnmet: conditionUnmet(gathered),
        costEarmarked: false,
        targeting: foundTargeting,
        usedUp: usedUp(id, affordance),
        ...(range.length > 0 ? { range } : {}),
        ...(foundSites ? { sites: foundSites } : {}),
        ...(gathered.some((a) => a.effect.activation === "quick_tech") ? { quickTech: true as const } : {}),
      } satisfies ActionDescriptor;
    });

  // Pilot-effect source = the combatant's frozen `base.pilotEffectIds` ∪ `base.frameEffectIds`
  // (talent-rank + core-bonus ids, plus frame-trait ids like `everest_initiative`), via the SAME
  // `combatantEffectIds` merge the `resolve/` trigger/modifier gathers use. Both are bare overlay
  // keys — see the file-header doc + `pilotEffectDisplay`/`frameEffectDisplay` above. Deliberately
  // bypasses `parseGrantedId`/the destroyed-system filter above: those parse a `<itemId>:<index>`
  // composite pointing at a compendium item's `actions[]`, which is the WRONG shape for a bare
  // `talentId:rank`/frame-trait key — and neither a pilot effect nor a frame trait is a system,
  // so "destroyed" doesn't apply. Only ids that gather at least one `action`-kind overlay effect
  // enumerate; one descriptor per id (the first gathered action), matching the one-row-per-id
  // shape the granted branch keeps. Display source is resolved per-id off WHICH list it came from
  // (never guessed) — a frame-trait id never reaches `pilotEffectDisplay`'s talent/core-bonus
  // lookup and vice versa.
  //
  // **Deliberately EXCLUDES `base.pilotGearEffectIds`**: pilot gear grants its ACTIONS through a
  // completely different vector — the composite `${itemId}:${index}` keys of `base.affordances`,
  // which the `granted` branch above already mints a row for, correctly capped via that record's own
  // inline `cap`. `pilotGearEffectIds` carries BARE item ids (it's the
  // effect/modifier GATHER vector — Mobility Hardsuit's Boost-gated Flying clause, etc. — read by
  // `resolve/`/`saves.ts`/`contest.ts`/`interpret.ts`/`ai/ev.ts`). Pooling it into this union too
  // mints a SECOND, bare, uncapped row for every gear item that also has a composite row — which
  // only shows up once a single carrier holds both id lists at once (a pilot). A gear item whose only
  // action is authored in the overlay (Mobility Hardsuit's Fly) gets its composite row from the
  // enumerate-time overlay mint (`carriedAffordances`), so it too stays one row. Only THIS mint
  // excludes pilot gear — every other `combatantEffectIds` call site pools all three, because gear's
  // passive/effect clauses must still gather.
  //
  // The id LIST (which pilotEffectIds/frameEffectIds ids actually gather an action) is
  // {@link carriedPilotFrameActionIds} (`./economy`) — shared so the bar (here) and the
  // carrier-ownership wall consume ONE filter, never two restatements. This loop keeps the
  // descriptor-building (display name/blurb/cost/targeting), which the wall doesn't need.
  //
  // **An NPC feature that ALSO minted granted records is skipped here** (`npc-command-menu` 1.5). A
  // Trait with `actions[]` (the Cataphract's Charge) rides `npcFeatureEffectIds` for its passive
  // clauses AND freezes `npcf_charge_cataphract:0` with its Recharge target, so this union minted a
  // second, bare row with no record behind it: it read live while the composite was discharged. The
  // composite row is the action; the bare id stays legal to fire (the wall's list is unchanged).
  const grantedItemIds = new Set(carried.flatMap((a) => (a.source.kind === "granted" ? [a.source.itemId] : [])));
  const frameIdSet = new Set(combatant.base.frameEffectIds ?? []);
  const pilotEffects: ActionDescriptor[] = [...carriedPilotFrameActionIds(combatant, overlay)].filter((id) => !grantedItemIds.has(id)).flatMap((id) => {
    const gathered = gatherActions({ ownerId: combatant.id, entityId: id, overlay });
    const first = gathered[0];
    if (!first) return []; // structurally unreachable — carriedPilotFrameActionIds already filtered on this; kept as a defensive no-op
    // A named action clause (`<entryId>#<name>`) is titled by its name, under the entry's.
    const clauseName = first.effect.name;
    const entryId = clauseName !== undefined && id.endsWith(`#${clauseName}`) ? id.slice(0, -clauseName.length - 1) : id;
    const entryDisplay = frameIdSet.has(entryId) ? frameEffectDisplay(entryId, compendium) : pilotEffectDisplay(entryId, compendium);
    const { name, sourceName, blurb } = entryId === id ? entryDisplay : { ...entryDisplay, name: clauseName!, sourceName: entryDisplay.name };
    // No pilot effect/frame trait authors a `deploy_object` atom today (a talent rank or core bonus
    // isn't a deployable carrier), but `sitesRequirement` is cheap and total, so this branch stays
    // consistent with the granted branch above rather than assuming that never changes.
    const pilotTargeting = targetingFromOverlay(gathered, live);
    const pilotSites = pilotTargeting === "sites" ? sitesRequirement(gathered, live) : undefined;
    // NOT a tech-attack site, and deliberately left unrouted. This loop only ever reaches a
    // `kind:"action"` overlay clause (`gathered[0]`, guarded above) and never reads compendium
    // `action.attack` at all — a talent-RANK tech attack (`t_hacker`'s Full Tech "Last Argument of
    // Kings", `t_hacker:3`) has no live path to `"attack"` here even in principle: `freezeActionIds`
    // never freezes `techAttackActions` for a pilot-effect id (only loadout weapons/systems), so
    // `legalTechAttackWeaponIds` (the wall) would reject it regardless of what this branch routed.
    // `t_hacker:3` is unauthored in the pack today, so `carriedPilotFrameActionIds` never even
    // surfaces it here (no `action`-kind clause to gather) — no advisory row, unlike the granted
    // branch's honest degrade. See `CombatantBase.techAttackActions`'s own doc for the full
    // talent-rank-vs-talent-integrated-system boundary.
    return [
      {
        id,
        source: "granted",
        name,
        ...(sourceName ? { sourceName } : {}),
        cost: activationCost(first.effect.activation),
        category: "GRANTED",
        ...(blurb !== undefined ? { blurb } : {}),
        route: routeForActivation(first.effect.activation),
        resolvable: true,
        // The Everest's "Boost (Hyperspec Fuel Injector)" row is THIS branch (a frame-trait id off
        // `base.frameEffectIds`). `resolvable` stays `true` — the clause has a real engine body —
        // and its `has_status "Power Up"` gate is carried honestly by `conditionUnmet` instead.
        conditionUnmet: conditionUnmet(gathered),
        costEarmarked: false,
        targeting: pilotTargeting,
        usedUp: usedUp(id),
        ...(pilotSites ? { sites: pilotSites } : {}),
      } satisfies ActionDescriptor,
    ];
  });

  // Status source: an action a status's entry authors for its carrier (Hack/Slash's reboot, Sniper's
  // Mark's Drop Prone), titled by the clause's name under the status's, as a named pilot-effect row is.
  const statusActions: ActionDescriptor[] = [...carriedStatusActionIds(combatant, overlay)].flatMap((id) => {
    const gathered = gatherActions({ ownerId: combatant.id, entityId: id, overlay });
    const first = gathered[0];
    if (!first) return [];
    const clauseName = first.effect.name;
    const status = clauseName !== undefined && id.endsWith(`#${clauseName}`) ? id.slice(0, -clauseName.length - 1) : id;
    return [
      {
        id,
        source: "granted",
        name: clauseName ?? status,
        ...(clauseName !== undefined ? { sourceName: status } : {}),
        cost: activationCost(first.effect.activation),
        category: "STATUS",
        route: routeForActivation(first.effect.activation),
        resolvable: true,
        conditionUnmet: conditionUnmet(gathered),
        costEarmarked: false,
        targeting: targetingFromOverlay(gathered, live),
        usedUp: usedUp(id),
      } satisfies ActionDescriptor,
    ];
  });

  // Deployed source (`ll1-drones` D6): an action an allied drone in reach carries for this
  // combatant (Restock), one row per drone, titled by the clause under the drone's name. The row
  // names its drone in `carrierId`; the activate targets it. Needs `state` (reach and side).
  const deployedActions: ActionDescriptor[] = (state !== undefined ? deployedActionSources(state, combatant.id, overlay) : []).flatMap(({ actionId, droneId }) => {
    const gathered = gatherActions({ ownerId: combatant.id, entityId: actionId, overlay });
    const first = gathered[0];
    if (!first) return [];
    const pctx = { state: state!, actors: { self: combatant.id, attacker: combatant.id, target: droneId } };
    return [
      {
        id: actionId,
        source: "granted",
        name: first.effect.name ?? actionId,
        sourceName: state!.combatants[droneId]?.name ?? droneId,
        cost: activationCost(first.effect.activation),
        category: "DEPLOYED",
        route: routeForActivation(first.effect.activation),
        resolvable: true,
        conditionUnmet: first.effect.condition !== undefined && !evaluatePredicate(first.effect.condition, pctx),
        costEarmarked: false,
        targeting: "single",
        usedUp: usedUp(actionId),
        carrierId: droneId,
      } satisfies ActionDescriptor,
    ];
  });

  const rows = [...core, ...granted, ...pilotEffects, ...statusActions, ...deployedActions];
  // A carrier that cannot make attacks (the rig's Unarmed, `cannotAttack`) gets no attack rows —
  // the same predicate the room's attack gate refuses on, so a row never offers what the server
  // would reject. Needs `state` (the fold reads the carrier off it); without it, rows degrade open.
  const unarmed = state !== undefined && cannotMakeAttacks({ state, actorId: combatant.id, overlay }) !== undefined;
  return unarmed ? rows.filter((d) => d.route !== "attack") : rows;
}
